'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

import { trackCollaborationInterest, trackVarahaPartnershipInteraction } from '@/lib/analytics';

const CollaborationHeroSection = () => {
  useEffect(() => {
    // Track collaboration page view for Varaha partnership discoverability
    trackVarahaPartnershipInteraction('page_view', 'collaboration_page');
    trackCollaborationInterest('page_load');
  }, []);

  return (
    <section className="relative bg-gradient-to-br from-secondary/10 via-primary/5 to-accent/10 pt-24 pb-16 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary/10 text-secondary rounded-full mb-6 animate-fade-in">
            <Icon name="BuildingOffice2Icon" size={20} />
            <span className="text-sm font-semibold">Strategic Partnerships</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-text-primary mb-6 animate-fade-in">
            Let's Collaborate
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-text-secondary mb-8 leading-relaxed animate-fade-in">
            GenCarbon partners with industry leaders to deliver comprehensive sustainability 
            solutions across banking, environmental management, and sustainable agriculture. 
            Together, we drive innovation and create measurable impact.
          </p>

          {/* Value Proposition */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                icon: 'ChartBarIcon',
                title: 'Banking & Government',
                description: 'Financial sector ESG compliance and government sustainability initiatives',
              },
              {
                icon: 'BeakerIcon',
                title: 'Water & Environment',
                description: 'Comprehensive water conservation and environmental restoration solutions',
              },
              {
                icon: 'CpuChipIcon',
                title: 'AI-Powered Agriculture',
                description: 'Sustainable fisheries and aquaculture using advanced AI algorithms',
              },
            ]?.map((value, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-natural-lg transition-all duration-300 ease-organic animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={value?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-text-primary mb-2 text-lg">
                  {value?.title}
                </h3>
                <p className="text-sm text-text-secondary">{value?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaborationHeroSection;